import React, { useState } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Home from './routes/Home';
import Projects from './routes/Projects';
import Services from './routes/Services';
import Brand from './components/Brand';
import VisitorCounter from './components/VisitorCounter';
import './index.css';

const NAV = [
  { to: '/',         label: 'Overview' },
  { to: '/projects', label: 'Research' },
  { to: '/services', label: 'Team' },
];

const navStyle = (active) => ({
  fontFamily: "'Bricolage Grotesque', system-ui, sans-serif",
  fontSize: 15,
  fontWeight: 500,
  color: active ? 'var(--ink)' : 'var(--ink-2)',
  textDecoration: 'none',
  padding: '8px 2px',
  position: 'relative',
  display: 'inline-block',
  transition: 'color 0.18s ease',
});

const Masthead = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <motion.header
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      style={{
        background: 'rgba(222, 231, 241, 0.78)',
        backdropFilter: 'blur(16px) saturate(1.05)',
        WebkitBackdropFilter: 'blur(16px) saturate(1.05)',
        borderBottom: '1px solid var(--rule-soft)',
        position: 'sticky',
        top: 0,
        zIndex: 50,
      }}
    >
      <div
        className="flex items-center justify-between"
        style={{ height: 76, padding: '0 24px', maxWidth: 1400, margin: '0 auto' }}
      >
        <Link to="/" onClick={() => setOpen(false)} style={{ textDecoration: 'none' }}>
          <Brand size={32} />
        </Link>

        <nav className="hidden lg:flex items-center" style={{ gap: 36 }}>
          {NAV.map(({ to, label }) => {
            const active = location.pathname === to;
            return (
              <Link key={to} to={to} style={navStyle(active)}>
                {label}
                {active && (
                  <motion.span
                    layoutId="nav-underline"
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      left: 0, right: 0,
                      bottom: 2,
                      height: 2,
                      borderRadius: 1,
                      background: 'var(--teal)',
                    }}
                  />
                )}
              </Link>
            );
          })}
          <a href="/services" className="btn-primary" style={{ padding: '11px 20px', fontSize: 13.5 }}>
            Contact <span className="arrow">→</span>
          </a>
        </nav>

        <button
          type="button"
          className="lg:hidden"
          aria-label={open ? 'Close menu' : 'Open menu'}
          onClick={() => setOpen((o) => !o)}
          style={{
            background: 'var(--glass)',
            border: '1px solid var(--rule-strong)',
            color: 'var(--ink)',
            padding: 9,
            cursor: 'pointer',
            borderRadius: 999,
          }}
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className="lg:hidden"
            style={{ borderTop: '1px solid var(--rule-soft)', overflow: 'hidden' }}
          >
            <ul style={{ listStyle: 'none', margin: 0, padding: '8px 24px 16px' }}>
              {NAV.map(({ to, label }) => (
                <li key={to} style={{ padding: '10px 0' }}>
                  <Link to={to} onClick={() => setOpen(false)} style={navStyle(location.pathname === to)}>
                    {label}
                  </Link>
                </li>
              ))}
              <li style={{ padding: '14px 0 4px' }}>
                <a href="/services" className="btn-primary" style={{ padding: '11px 20px', fontSize: 13.5 }}>
                  Contact <span className="arrow">→</span>
                </a>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

const footerLink = {
  fontFamily: "'Geist', system-ui, sans-serif",
  fontSize: 13.5,
  color: 'var(--ink-2)',
  textDecoration: 'none',
  transition: 'color 0.18s ease',
};

const Footer = () => (
  <footer
    style={{
      marginTop: 'clamp(48px, 7vh, 88px)',
      borderTop: '1px solid var(--rule)',
      paddingTop: 20,
      paddingBottom: 20,
      position: 'relative',
      zIndex: 1,
    }}
  >
    <div className="page footer-bar">
      <span>© 2025 Ciphercurve GmbH · St Gallerstrasse 66, 8400 Winterthur</span>
      <span style={{ display: 'flex', gap: 22 }}>
        <a
          href="https://cal.com/jonas-p-oswsyh/30min"
          target="_blank"
          rel="noopener noreferrer"
          style={footerLink}
          className="footer-link"
        >
          Book a call
        </a>
        <a
          href="https://github.com/jonas089"
          target="_blank"
          rel="noopener noreferrer"
          style={footerLink}
          className="footer-link"
        >
          GitHub
        </a>
      </span>
    </div>
  </footer>
);

const App = () => (
  <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
    <Masthead />
    <main style={{ flex: 1 }}>
      <Routes>
        <Route index element={<Home />} />
        <Route path="projects" element={<Projects />} />
        <Route path="services" element={<Services />} />
      </Routes>
    </main>
    <Footer />
    <VisitorCounter />
  </div>
);

export default App;
